import { useState } from 'react';
import type { Draft, SectionKey } from '../data/types';
import { SECTIONS } from '../data/sections';
import { FormSection } from './FormSection';
import { LetterPreview } from './LetterPreview';
import {
  copyToClipboard,
  downloadFile,
  draftToMarkdown,
  draftToText,
  safeFilename,
} from '../lib/exportFormats';
import styles from './Composer.module.css';

interface ComposerProps {
  draft: Draft;
  onChange: (next: Draft) => void;
}

export function Composer({ draft, onChange }: ComposerProps) {
  const [feedback, setFeedback] = useState<string | null>(null);

  function ping(msg: string) {
    setFeedback(msg);
    window.setTimeout(() => setFeedback(null), 1800);
  }

  function update(partial: Partial<Draft>) {
    onChange({ ...draft, ...partial, updatedAt: new Date().toISOString() });
  }

  function setSection(key: SectionKey, value: string) {
    update({ sections: { ...draft.sections, [key]: value } });
  }

  async function handleCopy() {
    const ok = await copyToClipboard(draftToText(draft));
    ping(ok ? 'copied to clipboard' : 'copy failed');
  }

  function handleMarkdown() {
    const name = safeFilename(draft.title || 'negotiation-letter');
    downloadFile(`${name}.md`, draftToMarkdown(draft), 'text/markdown');
    ping('downloaded markdown');
  }

  function handlePrint() {
    window.print();
  }

  return (
    <div className={styles.layout}>
      <section className={styles.form}>
        <div className={styles.metaRow}>
          <label className={styles.metaLabel}>
            title
            <input
              type="text"
              className={styles.metaInput}
              value={draft.title}
              onChange={(e) => update({ title: e.target.value })}
              placeholder="give this draft a name"
              maxLength={80}
            />
          </label>
          <label className={styles.metaLabel}>
            recipient (optional)
            <input
              type="text"
              className={styles.metaInput}
              value={draft.recipient ?? ''}
              onChange={(e) => update({ recipient: e.target.value })}
              placeholder="whom is this for?"
              maxLength={80}
            />
          </label>
        </div>

        {SECTIONS.map((sec) => (
          <FormSection
            key={sec.key}
            section={sec}
            value={draft.sections[sec.key] ?? ''}
            onChange={(value) => setSection(sec.key, value)}
          />
        ))}

        <div className={styles.actions}>
          <button type="button" className={styles.action} onClick={handlePrint}>
            print
          </button>
          <button type="button" className={styles.action} onClick={handleCopy}>
            copy as text
          </button>
          <button type="button" className={styles.action} onClick={handleMarkdown}>
            download .md
          </button>
        </div>
      </section>

      <aside className={styles.previewWrap}>
        <p className={styles.previewLabel}>preview · updates as you write</p>
        <LetterPreview draft={draft} />
      </aside>

      {feedback && (
        <div className={styles.feedback} role="status" aria-live="polite">
          {feedback}
        </div>
      )}
    </div>
  );
}
